/**
 * ILF Video Player — <ilf-video-player>
 *
 * Fullscreen modal video player built on a native <dialog>. The video's
 * sources live in an inert <template> so nothing downloads until the player
 * opens; closing removes the sources again and aborts any buffering.
 *
 * Open trigger: any element carrying data-ilf-video-player-open="<player id>".
 * A single delegated document listener wires every trigger on the page.
 *
 * Configuration attributes on the element (set by the snippet):
 * - data-muted: playback starts muted; viewers can unmute
 * - data-volume: starting volume, 0–1 (ignored on iOS, where volume is read-only)
 * - data-hide-controls: styling only, the controls bar is not shown
 * Looping is the <video loop> attribute itself.
 *
 * Playback starts on open. When the browser blocks it, no "play" event
 * fires, so is-playing stays off and the CSS shows the centred play button
 * over the poster.
 *
 * Document CustomEvents (detail: { id, player, video }):
 * - ilf:video-player:open
 * - ilf:video-player:close
 * - ilf:video-player:ended
 *
 * State travels through classes on the element (is-open, is-playing,
 * is-muted, is-buffering, is-fullscreen, controls-hidden, is-ios);
 * continuous values (--played, --buffered, --volume) travel through
 * custom properties consumed by assets/ilf-video-player.css.
 *
 * Rendered by snippets/ilf-video-player.liquid.
 */
if (!customElements.get('ilf-video-player')) {
  const SEEK_STEP_SECONDS = 5;
  const CONTROLS_HIDE_DELAY_MS = 3000;
  const SCROLL_LOCK_CLASS = 'ilf-video-player-scroll-lock';

  // ==========================================
  // Services (pure helpers)
  // ==========================================

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const total = Math.floor(seconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = String(total % 60).padStart(2, '0');
    if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${secs}`;
    return `${minutes}:${secs}`;
  };

  // iPadOS reports itself as MacIntel; the touch-point check catches it
  const isIosDevice = () =>
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const bufferedFraction = (video) => {
    if (!video.duration || video.buffered.length === 0) return 0;
    return video.buffered.end(video.buffered.length - 1) / video.duration;
  };

  // ==========================================
  // Controller
  // ==========================================

  class IlfVideoPlayer extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.dialog = this.querySelector('.player-dialog');
      this.video = this.querySelector('.player-video');
      this.sourcesTemplate = this.querySelector('.player-sources');
      if (!this.dialog || !this.video || !this.sourcesTemplate) {
        console.warn('IlfVideoPlayer: required markup missing inside <ilf-video-player>');
        return;
      }

      this.playButton = this.querySelector('.play-button');
      this.muteButton = this.querySelector('.mute-button');
      this.fullscreenButton = this.querySelector('.fullscreen-button');
      this.closeButton = this.querySelector('.close-button');
      this.bigPlayButton = this.querySelector('.big-play-button');
      this.seekInput = this.querySelector('.seek-input');
      this.volumeInput = this.querySelector('.volume-input');
      this.timeCurrent = this.querySelector('.time-current');
      this.timeDuration = this.querySelector('.time-duration');

      this.sourcesLoaded = false;
      this.scrubbing = false;
      this.suppressNextVideoClick = false;
      this.hideControlsTimer = null;
      this.trigger = null;

      if (isIosDevice()) this.classList.add('is-ios');

      // AbortController makes init fully re-entrant across editor re-renders:
      // every listener dies with abort(), so reconnecting never duplicates them
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      document.addEventListener('fullscreenchange', () => this.onFullscreenChange(), { signal });

      this.dialog.addEventListener('close', () => this.onDialogClose(), { signal });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      }, { signal });
      this.dialog.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.dialog.addEventListener('pointermove', () => this.showControls(), { signal });
      this.dialog.addEventListener('touchstart', () => {
        // A tap while controls are hidden only reveals them; the click that
        // follows must not also toggle playback
        if (this.classList.contains('controls-hidden')) this.suppressNextVideoClick = true;
        this.showControls();
      }, { signal, passive: true });

      this.video.addEventListener('click', () => {
        if (this.suppressNextVideoClick) {
          this.suppressNextVideoClick = false;
          return;
        }
        this.togglePlay();
      }, { signal });
      this.video.addEventListener('play', () => {
        this.classList.add('is-playing');
        this.showControls();
      }, { signal });
      this.video.addEventListener('pause', () => {
        this.classList.remove('is-playing');
        this.showControls();
      }, { signal });
      this.video.addEventListener('timeupdate', () => this.updateProgress(), { signal });
      this.video.addEventListener('durationchange', () => this.updateDuration(), { signal });
      this.video.addEventListener('progress', () => this.updateBuffered(), { signal });
      this.video.addEventListener('waiting', () => this.classList.add('is-buffering'), { signal });
      this.video.addEventListener('playing', () => this.classList.remove('is-buffering'), { signal });
      this.video.addEventListener('canplay', () => this.classList.remove('is-buffering'), { signal });
      this.video.addEventListener('volumechange', () => this.updateVolumeUi(), { signal });
      this.video.addEventListener('ended', () => this.onEnded(), { signal });

      this.playButton?.addEventListener('click', () => this.togglePlay(), { signal });
      this.muteButton?.addEventListener('click', () => this.toggleMute(), { signal });
      this.fullscreenButton?.addEventListener('click', () => this.toggleFullscreen(), { signal });
      this.closeButton?.addEventListener('click', () => this.close(), { signal });
      this.bigPlayButton?.addEventListener('click', () => this.togglePlay(), { signal });

      this.seekInput?.addEventListener('input', () => this.onSeekInput(), { signal });
      this.seekInput?.addEventListener('pointerdown', () => { this.scrubbing = true; }, { signal });
      this.seekInput?.addEventListener('pointerup', () => { this.scrubbing = false; }, { signal });
      this.seekInput?.addEventListener('change', () => { this.scrubbing = false; }, { signal });
      this.volumeInput?.addEventListener('input', () => this.onVolumeInput(), { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      clearTimeout(this.hideControlsTimer);
      if (this.video) this.video.pause();
      this.classList.remove('is-open');
      this.releaseScrollLock();
    }

    // ==========================================
    // Open / close lifecycle
    // ==========================================

    open(trigger) {
      if (!this.dialog || this.dialog.open) return;
      this.trigger = trigger || null;
      this.loadSources();
      this.dialog.showModal();
      this.classList.add('is-open');
      document.documentElement.classList.add(SCROLL_LOCK_CLASS);
      const volume = Number.parseFloat(this.dataset.volume);
      if (Number.isFinite(volume)) this.video.volume = Math.min(Math.max(volume, 0), 1);
      this.video.muted = this.hasAttribute('data-muted');
      this.updateVolumeUi();
      this.showControls();
      const playPromise = this.video.play();
      if (playPromise) playPromise.catch(() => {});
      this.dispatchPlayerEvent('open');
    }

    close() {
      if (this.dialog?.open) this.dialog.close();
    }

    onDialogClose() {
      clearTimeout(this.hideControlsTimer);
      this.unloadSources();
      this.classList.remove('is-open', 'is-playing', 'is-buffering', 'controls-hidden');
      this.releaseScrollLock();
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
      this.dispatchPlayerEvent('close');
      if (this.trigger && this.trigger.isConnected) this.trigger.focus();
      this.trigger = null;
    }

    loadSources() {
      if (this.sourcesLoaded) return;
      const poster = this.video.dataset.poster;
      if (poster) this.video.setAttribute('poster', poster);
      this.video.append(this.sourcesTemplate.content.cloneNode(true));
      this.video.load();
      this.sourcesLoaded = true;
    }

    unloadSources() {
      if (!this.sourcesLoaded) return;
      this.video.pause();
      this.video.querySelectorAll('source').forEach((source) => source.remove());
      this.video.removeAttribute('poster');
      // load() with no sources aborts the download and releases the buffer
      this.video.load();
      this.sourcesLoaded = false;
      this.style.setProperty('--played', 0);
      this.style.setProperty('--buffered', 0);
      if (this.seekInput) this.seekInput.value = '0';
      if (this.timeCurrent) this.timeCurrent.textContent = '0:00';
      if (this.timeDuration) this.timeDuration.textContent = '0:00';
    }

    releaseScrollLock() {
      // Another player may still be open (rare, but cheap to respect)
      if (!document.querySelector('ilf-video-player.is-open')) {
        document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
      }
    }

    // ==========================================
    // Playback controls
    // ==========================================

    togglePlay() {
      if (this.video.paused) {
        const playPromise = this.video.play();
        if (playPromise) playPromise.catch(() => {});
      } else {
        this.video.pause();
      }
    }

    toggleMute() {
      if (this.video.muted || this.video.volume === 0) {
        this.video.muted = false;
        if (this.video.volume === 0) this.video.volume = 1;
      } else {
        this.video.muted = true;
      }
    }

    toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
        return;
      }
      if (this.dialog.requestFullscreen) {
        this.dialog.requestFullscreen().catch(() => {});
      } else if (this.video.webkitEnterFullscreen) {
        // iPhone: element fullscreen is unavailable; hand off to the native player
        this.video.webkitEnterFullscreen();
      }
    }

    onFullscreenChange() {
      this.classList.toggle('is-fullscreen', document.fullscreenElement === this.dialog);
    }

    seekBy(deltaSeconds) {
      const duration = this.video.duration;
      if (!duration) return;
      this.video.currentTime = Math.min(Math.max(this.video.currentTime + deltaSeconds, 0), duration);
      this.updateProgress();
    }

    onSeekInput() {
      const duration = this.video.duration;
      if (!duration || !this.seekInput) return;
      const fraction = Number(this.seekInput.value) / 100;
      this.video.currentTime = fraction * duration;
      this.style.setProperty('--played', fraction);
      this.showControls();
    }

    onVolumeInput() {
      if (!this.volumeInput) return;
      const volume = Number(this.volumeInput.value);
      this.video.volume = volume;
      this.video.muted = volume === 0;
      this.showControls();
    }

    onEnded() {
      this.classList.remove('is-playing');
      this.showControls();
      this.dispatchPlayerEvent('ended');
    }

    // ==========================================
    // UI sync
    // ==========================================

    updateProgress() {
      const { currentTime, duration } = this.video;
      const played = duration ? currentTime / duration : 0;
      this.style.setProperty('--played', played);
      if (this.seekInput && !this.scrubbing) {
        this.seekInput.value = String(played * 100);
        this.seekInput.setAttribute(
          'aria-valuetext',
          `${formatTime(currentTime)} of ${formatTime(duration)}`
        );
      }
      if (this.timeCurrent) this.timeCurrent.textContent = formatTime(currentTime);
    }

    updateDuration() {
      if (this.timeDuration) this.timeDuration.textContent = formatTime(this.video.duration);
    }

    updateBuffered() {
      this.style.setProperty('--buffered', bufferedFraction(this.video));
    }

    updateVolumeUi() {
      const muted = this.video.muted || this.video.volume === 0;
      this.classList.toggle('is-muted', muted);
      const volume = muted ? 0 : this.video.volume;
      this.style.setProperty('--volume', volume);
      if (this.volumeInput) this.volumeInput.value = String(volume);
    }

    showControls() {
      clearTimeout(this.hideControlsTimer);
      this.classList.remove('controls-hidden');
      if (this.video.paused || this.video.ended) return;
      this.hideControlsTimer = setTimeout(() => {
        if (!this.video.paused && !this.video.ended) this.classList.add('controls-hidden');
      }, CONTROLS_HIDE_DELAY_MS);
    }

    // ==========================================
    // Keyboard shortcuts
    // ==========================================

    onKeydown(event) {
      const target = event.target;
      const targetIsButton = target instanceof HTMLButtonElement;
      switch (event.key) {
        case ' ':
          // Space on a focused button activates the button natively
          if (targetIsButton) return;
          event.preventDefault();
          this.togglePlay();
          break;
        case 'k':
        case 'K':
          this.togglePlay();
          break;
        case 'm':
        case 'M':
          this.toggleMute();
          break;
        case 'f':
        case 'F':
          this.toggleFullscreen();
          break;
        case 'ArrowLeft':
        case 'ArrowRight':
          // Arrows on the volume slider keep their native meaning
          if (target === this.volumeInput) return;
          event.preventDefault();
          this.seekBy(event.key === 'ArrowLeft' ? -SEEK_STEP_SECONDS : SEEK_STEP_SECONDS);
          break;
        default:
          return;
      }
      this.showControls();
    }

    dispatchPlayerEvent(name) {
      document.dispatchEvent(
        new CustomEvent(`ilf:video-player:${name}`, {
          detail: { id: this.id, player: this, video: this.video },
        })
      );
    }
  }

  customElements.define('ilf-video-player', IlfVideoPlayer);

  // One delegated listener opens players from any trigger on the page
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-ilf-video-player-open]');
    if (!trigger) return;
    const player = document.getElementById(trigger.getAttribute('data-ilf-video-player-open'));
    if (player && typeof player.open === 'function') {
      event.preventDefault();
      player.open(trigger);
    }
  });
}
